import { useTellescopeInfiniteQuery, useTellescopeSession } from "@tellescope/tanstack-query";
import { TellescopeFormFieldBaseProps } from "../../types";
import { Label, Select } from "@tellescope/design-system";
import { Button } from "@tellescope/ui";
import { cn } from "@tellescope/utils";
import { AddToDatabaseForm } from "./AddToDatabaseForm";
import { labelForDatabaseRecord } from "./label-for-database-record";
import { useDebouncedValue } from "@tellescope/react-hooks";
import { transformDatabasePage } from "./transform-database-page";
import { useMemo, useState } from "react";
import { DatabaseRecord, DatabaseSelectResponse, Enduser, FormResponseValue } from "@tellescope/types-models";
import { filterDatabaseChoices } from "../../utils/filter-database-choices";
import { FieldDescription } from "../../FieldDescription";
import { useTranslation } from "react-i18next";

const DATABASE_SEARCH_MIN_CHARACTERS = 3;

export interface DatabaseSelectFieldProps extends TellescopeFormFieldBaseProps<"Database Select"> {
  formResponses?: FormResponseValue[];
  enduser?: Pick<Enduser, "state">;
  onDatabaseSelect?: (records: DatabaseRecord[]) => void;
}

export const DatabaseSelectField = ({
  id,
  title,
  description,
  htmlDescription,
  placeholder,
  value,
  onChange,
  className,
  options,
  onBlur,
  error,
  required,
  formResponses,
  enduser,
  onDatabaseSelect,
  autoFocus,
}: DatabaseSelectFieldProps) => {
  const { t } = useTranslation("forms");
  const databaseId = options?.databaseId;
  const fieldId = id;
  const [isAddingRecord, setIsAddingRecord] = useState(false);

  const session = useTellescopeSession();
  const staffCanCreate =
    !!session && session.type !== "enduser" && "access" in session.userInfo
      ? !!session.userInfo.access["database_records"]?.create
      : false;
  const canAddToDatabase = !!options?.allowAddToDatabase && !!databaseId && staffCanCreate;

  const addToDatabaseColumns = useMemo(
    () => [...new Set([options?.databaseLabel, ...(options?.databaseLabels ?? [])].filter((c): c is string => !!c))],
    [options?.databaseLabel, options?.databaseLabels]
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery);
  const isSearching = debouncedSearchQuery.trim().length >= DATABASE_SEARCH_MIN_CHARACTERS;
  const isBelowSearchThreshold =
    searchQuery.trim().length > 0 && searchQuery.trim().length < DATABASE_SEARCH_MIN_CHARACTERS;
  const isAwaitingSearch = !isBelowSearchThreshold && searchQuery !== debouncedSearchQuery;
  const choicesQuery = useTellescopeInfiniteQuery({
    model: "form_fields",
    method: "load_choices_from_database",
    request: {
      databaseId,
      fieldId: fieldId ?? "",
      search: isSearching ? debouncedSearchQuery : "",
    },
    paginateRequest: (lastId, request) => ({ ...request, lastId }),
    initialPageParam: undefined as undefined | string,
    getNextPageParam: (lastPage) => lastPage.choices[lastPage.choices.length - 1]?.id,
    enabled: !!databaseId && !!fieldId,
    select: (data) => data.pages.flatMap((page) => page.choices),
  });

  const choiceOptions = useMemo(
    () =>
      transformDatabasePage(
        {
          choices: filterDatabaseChoices({
            choices: choicesQuery.data ?? [],
            options,
            responses: formResponses ?? [],
            enduser,
          }),
        },
        { databaseLabel: options?.databaseLabel, databaseLabels: options?.databaseLabels }
      ),
    [choicesQuery.data, options, formResponses, enduser]
  );

  const selectedByRecordId = useMemo(
    () =>
      (value ?? []).reduce<Map<string, DatabaseSelectResponse>>((acc, entry) => {
        const recordId = entry.recordId ?? choiceOptions.find(({ label }) => label === entry.text)?.value;
        if (recordId) {
          acc.set(recordId, entry);
        }
        return acc;
      }, new Map()),
    [value, choiceOptions]
  );

  const selectedRecordIds = useMemo(() => [...selectedByRecordId.keys()], [selectedByRecordId]);

  const visibleOptions = useMemo(() => {
    const choiceIds = new Set(choiceOptions.map(({ value: optionValue }) => optionValue));
    const missingSelections = [...selectedByRecordId].reduce<{ value: string; label: string }[]>(
      (acc, [recordId, entry]) => {
        if (!choiceIds.has(recordId)) {
          acc.push({ value: recordId, label: entry.text });
        }
        return acc;
      },
      []
    );

    const allOptions = [...choiceOptions, ...missingSelections];

    if (!isBelowSearchThreshold) return allOptions;

    const query = searchQuery.trim().toLowerCase();
    return allOptions.filter(({ label }) => label.toLowerCase().includes(query));
  }, [choiceOptions, selectedByRecordId, isBelowSearchThreshold, searchQuery]);

  const handleOnValueChange = (recordIds: string[]) => {
    const incomingIds = options?.radio ? recordIds.slice(-1) : recordIds;

    const records = incomingIds.reduce<DatabaseSelectResponse[]>((acc, id) => {
      const existing = selectedByRecordId.get(id);
      if (existing) {
        acc.push(existing);
        return acc;
      }

      const match = visibleOptions.find((item) => item.value === id);
      if (match) {
        acc.push({
          text: match.label,
          recordId: match.value,
          databaseId,
        });
        return acc;
      }

      // The creator row selects the typed text as an id before any render lists it as an option, so an
      // id matching no record is the free-text "other" answer.
      if (options?.other) {
        acc.push({ text: id, recordId: id, databaseId: "" });
      }
      return acc;
    }, []);

    const previouslySelected = new Set(selectedRecordIds);
    const newlySelected = (choicesQuery.data ?? []).filter(
      (record) => incomingIds.includes(record.id) && !previouslySelected.has(record.id)
    );
    if (newlySelected.length) onDatabaseSelect?.(newlySelected);

    onChange(records);
  };

  const handleRecordCreated = (record: DatabaseRecord) => {
    setIsAddingRecord(false);
    const entry: DatabaseSelectResponse = {
      text: labelForDatabaseRecord(
        { databaseLabel: options?.databaseLabel, databaseLabels: options?.databaseLabels },
        record
      ),
      recordId: record.id,
      databaseId,
    };
    onChange(options?.radio ? [entry] : [...(value ?? []), entry]);
    choicesQuery.refetch();
  };

  return (
    <div className="space-y-2">
      {!!(title || placeholder) && <Label required={required}>{title || placeholder}</Label>}
      <FieldDescription description={description} htmlDescription={htmlDescription} />
      <Select
        multiple
        value={selectedRecordIds}
        onValueChange={handleOnValueChange}
        onOpenChange={(open) => {
          if (!open) onBlur?.();
        }}
        fallbackLabel={(recordId) => selectedByRecordId.get(recordId)?.text ?? recordId}
        creator={options?.other ? { label: (text) => t('Add "{{value}}"', { value: text }) } : undefined}
      >
        <Select.Trigger autoFocus={autoFocus} aria-invalid={!!error} className={cn("w-full", className)}>
          <Select.Value placeholder={placeholder ?? t("Select items...")} />
        </Select.Trigger>
        <Select.Content
          filter="external"
          search={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder={t("Search...")}
          emptyText={
            isBelowSearchThreshold
              ? t("Keep typing to search — {{count}} more character", {
                  count: DATABASE_SEARCH_MIN_CHARACTERS - searchQuery.trim().length,
                })
              : t("No results found")
          }
          loading={isAwaitingSearch || choicesQuery.isLoading}
        >
          {visibleOptions.map((option) => (
            <Select.Item key={option.value} value={option.value}>
              {option.label}
            </Select.Item>
          ))}
        </Select.Content>
      </Select>
      {!!canAddToDatabase &&
        (isAddingRecord ? (
          <AddToDatabaseForm
            databaseId={databaseId ?? ""}
            columns={addToDatabaseColumns}
            onCancel={() => setIsAddingRecord(false)}
            onCreated={handleRecordCreated}
          />
        ) : (
          <Button type="button" variant="outline" onClick={() => setIsAddingRecord(true)}>
            {t("Add to database")}
          </Button>
        ))}

      {!!error && <p className="text-sm text-destructive mt-1">{error}</p>}
    </div>
  );
};
